// ─── useRunGate ───────────────────────────────────────────────────────────────
// Everything that stands between "the user asked to generate" and
// `handleGenerate()` actually running:
//
//   1. the **credit gate** (PB4) — /balance's `can_generate` / `block_reason`;
//   2. the pre-existing **leave-guard** — a confirm when there is output on
//      screen that has not been copied whole («Copia tutto») yet.
//
// ⚠️ Both call sites go through here. The Generate button's `onClick` AND the
// Ctrl/Cmd+Enter shortcut call the same `guardedGenerate`, and the shortcut's
// listener lives in THIS hook rather than in the orchestrator, so the two paths
// cannot drift: gating only the button would leave the keyboard wide open.
//
// ⚠️ Order matters. The credit gate runs FIRST. Asking "your output will be
// overwritten, proceed?" and then refusing the run would discard the user's
// work for a run that was never going to start.
//
// ⚠️ The gate reads `can_generate` and NOTHING else. Not `remaining_credits`,
// which is null for two opposite reasons (an unlimited plan that may run
// forever, and no plan at all — see useEntitlements). And it never pre-blocks
// on anything the server treats as exempt: mock-mode backends and the `default`
// tenant run regardless of billing while /balance keeps reporting the true
// state, so `can_generate: false` there legitimately coexists with successful
// runs. We surface the verdict; we do not second-guess it, and we do not
// suppress it either.

import { useCallback, useEffect, useState } from 'react';
import useEntitlements from './useEntitlements.js';
import { refusalFromBlockReason } from '../utils/runRefusal.js';

/**
 * @param {object} args
 * @param {(...a: any[]) => void} args.dispatch store dispatch
 * @param {() => void} args.handleGenerate the real generation trigger
 * @param {() => boolean} args.hasUndownloadedOutput leave-guard predicate
 * @param {{current: Set<string>}} args.downloadedIdsRef reset when a run starts
 * @returns {{
 *   guardedGenerate: () => void,
 *   pendingAction: {action: () => void, type: string}|null,
 *   setPendingAction: (v: any) => void,
 *   confirmAction: () => void,
 *   cancelAction: () => void,
 *   blocked: boolean,
 *   blockReason: string|null,
 *   periodEnd: string|null,
 * }}
 */
export function useRunGate({ dispatch, handleGenerate, hasUndownloadedOutput, downloadedIdsRef }) {
  const { canGenerate, blockReason, periodEnd } = useEntitlements();

  // `canGenerate` is true while loading, on an old backend, and after a failed
  // /balance — the hook fails open, so a credits outage cannot lock the app.
  const blocked = canGenerate === false;

  const [pendingAction, setPendingAction] = useState(null); // { action, type }

  const guardedGenerate = useCallback(() => {
    if (blocked) {
      // Show the verdict where the user just tried to act. The store holds a
      // plain descriptor, never the rendered sentence: copy is resolved at
      // render so it follows the active language.
      dispatch({
        type: 'SET_KEY',
        key: 'runRefusal',
        // A blocked balance with no `block_reason` (an in-between backend)
        // still gets the generic blocked treatment rather than silence.
        value: refusalFromBlockReason(blockReason || 'BLOCKED'),
      });
      return;
    }

    // …then the leave-guard, unchanged.
    if (hasUndownloadedOutput()) {
      setPendingAction({
        action: () => { downloadedIdsRef.current.clear(); handleGenerate(); },
        type: 'generate',
      });
    } else {
      downloadedIdsRef.current.clear();
      handleGenerate();
    }
  }, [blocked, blockReason, dispatch, hasUndownloadedOutput, handleGenerate, downloadedIdsRef]);

  const confirmAction = useCallback(() => {
    if (pendingAction) pendingAction.action();
    setPendingAction(null);
  }, [pendingAction]);

  const cancelAction = useCallback(() => setPendingAction(null), []);

  // Ctrl/Cmd+Enter — the second call site, gated by construction because it
  // calls the same funnel.
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') guardedGenerate(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [guardedGenerate]);

  return {
    guardedGenerate,
    pendingAction,
    setPendingAction,
    confirmAction,
    cancelAction,
    blocked,
    blockReason,
    periodEnd,
  };
}

export default useRunGate;
